import AsyncStorage from '@react-native-async-storage/async-storage';
import { BookService } from './bookService';
import { FireBook } from '../models/FirestoreModels';

type DailyFreeRecord = {
  date: string; // YYYY-MM-DD
  bookId: string;
  languageCode: string;
  bookTitle?: string;
  coverImageUrl?: string;
};

const STORAGE_KEY = '@mentorist_daily_free_book';
const BOOK_OF_DAY_KEY = 'book_of_day_history'; // shared with bookOfDayService

const todayIso = () => new Date().toISOString().slice(0, 10);

const normalizeBookId = (bookId: string): string => {
  if (!bookId) return '';
  return bookId.includes('_') ? bookId.split('_')[0] : bookId;
};

export class DailyFreeBookService {
  /**
   * Get today's free book record.
   * Uses the existing Book of the Day cache for consistency with the Free Daily widget.
   */
  static async getTodayFreeBook(languageCode: string = 'en'): Promise<DailyFreeRecord | null> {
    const today = todayIso();

    // Prefer the Book of the Day selection (same as existing widget)
    try {
      const raw = await AsyncStorage.getItem(BOOK_OF_DAY_KEY);
      if (raw) {
        const history = JSON.parse(raw) as {
          lastDate?: string;
          currentBookId?: string;
        };
        if (history?.lastDate === today && history.currentBookId) {
          const normalizedBookId = normalizeBookId(history.currentBookId);
          const bookResult = await BookService.getBookDetail(normalizedBookId, languageCode);
          const book: FireBook | undefined = bookResult.success ? bookResult.data?.book : undefined;
          return {
            date: today,
            bookId: normalizedBookId,
            languageCode,
            bookTitle: book?.title,
            coverImageUrl: (book as any)?.coverImageUrl || (book as any)?.coverImageFullUrl,
          };
        }
      }
    } catch (error) {
      console.warn('[DailyFreeBookService] Failed to load Book of Day cache', error);
    }

    // Fallback: try our own cache (if ever set)
    try {
      const raw = await AsyncStorage.getItem(`${STORAGE_KEY}_${languageCode}`);
      if (raw) {
        const parsed = JSON.parse(raw) as DailyFreeRecord;
        if (parsed?.date === today) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('[DailyFreeBookService] Failed to read legacy cache', error);
    }

    return null;
  }

  /**
   * Check if a book is today's free selection.
   */
  static async isBookFreeToday(bookId: string, languageCode: string = 'en'): Promise<boolean> {
    const record = await this.getTodayFreeBook(languageCode);
    if (!record) return false;
    return normalizeBookId(bookId) === record.bookId;
  }
}


